import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  alpha,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  Link,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Pagination,
  Select,
  FormControl,
  useTheme,
  useMediaQuery,
  InputLabel,
  GlobalStyles
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { campaignApi, referenceApi, api } from '../services/api';
import { 
  formatCurrency, 
  determineCampaignStatus
} from '../utils/calculations';
import CampaignIcon from '@mui/icons-material/Campaign';
import SnapshotCalendarSelector from '../components/SnapshotCalendarSelector';

// Define consistent font sizes
const FONT_SIZES = {
  header: '0.9rem',
  subtitle: '0.75rem', 
  body: '0.75rem',
  caption: '0.7rem',
  small: '0.65rem',
  table: '0.7rem',
  tableHeader: '0.7rem',
  number: '0.7rem'
};

const CampaignsSnapshot = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentBrand, setCurrentBrand] = useState('All');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [brandsMap, setBrandsMap] = useState({});
  const [subBrandsMap, setSubBrandsMap] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSnapshotMonth, setSelectedSnapshotMonth] = useState(searchParams.get('snapshotMonth') || null);
  const [snapshotMonths, setSnapshotMonths] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);

  // Available brands for filtering - reordered to prioritize Heineken
  const brands = ['All', 'Heineken', 'Tiger', 'Larue', 'Strongbow', 'EDW', 'Bivina', 'BiaViet'];

  // Function to get campaign type display name
  const getCampaignTypeDisplay = (campaignTypeValue) => {
    if (!campaignTypeValue) return 'Thematic';
    if (typeof campaignTypeValue === 'string' && isNaN(campaignTypeValue)) {
      return campaignTypeValue;
    }
    const typeId = parseInt(campaignTypeValue);
    const foundType = campaignTypes.find(type => 
      type.campaign_type_id === typeId || type.id === typeId
    );
    return foundType ? (foundType.campaign_type_name || foundType.name) : 'Thematic';
  };

  // Fetch snapshot months on page load
  useEffect(() => {
    api.get('/system/snapshot/months/')
      .then(res => setSnapshotMonths(res.data.months || []))
      .catch(() => setSnapshotMonths([]));
  }, []);

  // Fetch reference data (brands, sub-brands, campaign types)
  const fetchReferenceData = async () => {
    try {
      // Fetch brands
      const brandsResponse = await referenceApi.getBrands();
      if (brandsResponse.data && Array.isArray(brandsResponse.data)) {
        const brandsMapping = {};
        brandsResponse.data.forEach(brand => {
          brandsMapping[brand.brand_id] = brand.brand_name;
        });
        setBrandsMap(brandsMapping);
        
        // Fetch sub-brands for each brand
        const allSubBrands = {};
        for (const brand of brandsResponse.data) {
          const subBrandsResponse = await referenceApi.getSubBrands(brand.brand_id);
          if (subBrandsResponse.data && Array.isArray(subBrandsResponse.data)) {
            subBrandsResponse.data.forEach(subBrand => {
              allSubBrands[subBrand.sub_brand_id] = subBrand.sub_brand_name;
            });
          }
        }
        setSubBrandsMap(allSubBrands);
      }

      // Fetch campaign types
      try {
        const campaignTypesResponse = await referenceApi.getCampaignTypes();
        if (campaignTypesResponse.data && Array.isArray(campaignTypesResponse.data)) {
          setCampaignTypes(campaignTypesResponse.data);
        }
      } catch (typeErr) {
        console.error('Error fetching campaign types:', typeErr);
        setCampaignTypes([
          { campaign_type_id: 1, campaign_type_name: 'Thematic' },
          { campaign_type_id: 2, campaign_type_name: 'AWO' },
          { campaign_type_id: 3, campaign_type_name: 'Credential' },
          { campaign_type_id: 4, campaign_type_name: 'Festive/ Celebration' }
        ]);
      }
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  // Fetch snapshot campaigns from API
  const fetchSnapshotCampaigns = useCallback(async (snapshotMonth) => {
    if (!snapshotMonth) return;
    
    setLoading(true);
    try {
      const response = await campaignApi.getCampaignsBySnapshot(snapshotMonth);
      console.log("=== Debug snapshot campaigns API response ===");
      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Response data type:", typeof response.data);
      if (Array.isArray(response.data)) {
        console.log("Response data length:", response.data.length);
        if (response.data.length > 0) {
          console.log("First campaign sample:", response.data[0]);
        }
      }
      
      // Handle different response formats
      let snapshotCampaigns = [];
      
      if (response.data && response.data.snapshot && response.data.snapshot.campaigns && Array.isArray(response.data.snapshot.campaigns)) {
        // Old format: wrapped in snapshot object
        snapshotCampaigns = response.data.snapshot.campaigns;
      } else if (Array.isArray(response.data)) {
        // New format: direct array of campaigns
        snapshotCampaigns = response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        // Paginated format
        snapshotCampaigns = response.data.results;
      }
      
      if (Array.isArray(snapshotCampaigns) && snapshotCampaigns.length > 0) {
        
        // Recalculate campaign totals from media types for snapshot data
        const campaignsWithCorrectTotals = snapshotCampaigns.map(campaign => {
          // Handle the new API format
          const campaignData = {
            id: campaign.id,
            campaign_name: campaign.Campaign_Name || campaign.campaign_name || 'Unknown Campaign',
            brand_name: campaign.Brand || campaign.brand_name || campaign.brand || 'Unknown Brand',
            sub_brand_name: campaign.Sub_Brand || campaign.sub_brand_name || campaign.sub_brand,
            campaign_type: campaign.Campaign_Type || campaign.campaign_type,
            objective: campaign.Objective_KPI || campaign.objective,
            start_date: campaign.Start_Date || campaign.start_date,
            end_date: campaign.End_Date || campaign.end_date,
            given_lc: campaign.Given_Cost_Excl_VAT_VND || campaign.given_lc || 0,
            planning_gross_cost_lc: campaign.Planning_Gross_Cost_Excl_VAT_VND || campaign.planning_gross_cost_lc || 0,
            actual_gross_cost_lc: campaign.Actual_Gross_Cost_Excl_VAT_VND || campaign.actual_gross_cost_lc || 0,
            ...campaign
          };
          
          // Always recalculate totals from media_types if available to ensure accuracy
          if (campaign.media_types && typeof campaign.media_types === 'object') {
            const mediaTypesArray = Object.values(campaign.media_types);
            
            console.log(`\n=== Recalculating totals for snapshot campaign: ${campaignData.campaign_name} ===`);
            console.log('Campaign data before recalculation:', {
              original_given: campaign.Given_Cost_Excl_VAT_VND || campaign.given_lc || campaign.total_given,
              original_planning: campaign.Planning_Gross_Cost_Excl_VAT_VND || campaign.planning_gross_cost_lc || campaign.planning_total,
              original_actual: campaign.Actual_Gross_Cost_Excl_VAT_VND || campaign.actual_gross_cost_lc || campaign.actual_total,
              media_types_count: mediaTypesArray.length
            });
            
            let totalGiven = 0;
            let totalPlanning = 0;
            let totalActual = 0;
            
            mediaTypesArray.forEach(mt => {
              if (mt && mt.monthly_data && Array.isArray(mt.monthly_data)) {
                // Sum from monthly data for more accuracy
                mt.monthly_data.forEach(monthData => {
                  const givenValue = monthData.given_cost || monthData.Given_Cost_Excl_VAT_VND || monthData.given;
                  const planningValue = monthData.Planning_Gross_Cost_Excl_VAT_VND || monthData.planning_gross_cost;
                  const actualValue = monthData.Actual_Gross_Cost_Excl_VAT_VND || monthData.actual_gross_cost;
                  
                  if (givenValue && givenValue !== 'nan' && !isNaN(parseFloat(givenValue))) {
                    totalGiven += parseFloat(givenValue);
                  }
                  if (planningValue && planningValue !== 'nan' && !isNaN(parseFloat(planningValue))) {
                    totalPlanning += parseFloat(planningValue);
                  }
                  if (actualValue && actualValue !== 'nan' && !isNaN(parseFloat(actualValue))) {
                    totalActual += parseFloat(actualValue);
                  }
                });
              } else if (mt) {
                // Fallback: use media type level totals if monthly_data is not available
                const mtGiven = mt.media_type_given || mt.Given_Cost_Excl_VAT_VND || mt.given || 0;
                const mtPlanning = mt.Planning_Gross_Cost_Excl_VAT_VND || mt.planning_gross_cost || 0;
                const mtActual = mt.Actual_Gross_Cost_Excl_VAT_VND || mt.actual_gross_cost || 0;
                
                if (mtGiven && mtGiven !== 'nan' && !isNaN(parseFloat(mtGiven))) {
                  totalGiven += parseFloat(mtGiven);
                }
                if (mtPlanning && mtPlanning !== 'nan' && !isNaN(parseFloat(mtPlanning))) {
                  totalPlanning += parseFloat(mtPlanning);
                }
                if (mtActual && mtActual !== 'nan' && !isNaN(parseFloat(mtActual))) {
                  totalActual += parseFloat(mtActual);
                }
              }
            });
            
            console.log('Recalculated totals:', {
              totalGiven,
              totalPlanning,
              totalActual
            });
            
            // Only use recalculated totals if they are greater than 0
            const finalTotals = {
              ...campaignData,
              total_given: totalGiven > 0 ? totalGiven.toString() : (campaign.total_given || campaign.Given_Cost_Excl_VAT_VND || campaign.given_lc || 0).toString(),
              planning_total: totalPlanning > 0 ? totalPlanning.toString() : (campaign.planning_total || campaign.Planning_Gross_Cost_Excl_VAT_VND || campaign.planning_gross_cost_lc || 0).toString(),
              actual_total: totalActual > 0 ? totalActual.toString() : (campaign.actual_total || campaign.Actual_Gross_Cost_Excl_VAT_VND || campaign.actual_gross_cost_lc || 0).toString()
            };
            
            console.log('Final campaign totals:', {
              final_given: finalTotals.total_given,
              final_planning: finalTotals.planning_total,
              final_actual: finalTotals.actual_total
            });
            
            return finalTotals;
          }
          
          // If no media_types available, check if API already provided proper totals
          if (campaign.total_given && campaign.planning_total && campaign.actual_total) {
            console.log(`Using API provided totals for campaign: ${campaignData.campaign_name}`);
            return campaignData;
          }
          
          return campaignData;
        });
        
        setCampaigns(campaignsWithCorrectTotals);
      } else {
        console.error('No valid snapshot campaigns found:', response.data);
        setCampaigns([]);
        setError(`No campaigns found for snapshot month ${snapshotMonth}`);
      }
    } catch (err) {
      setError('Failed to load snapshot campaigns. Please try again.');
      console.error('Error fetching snapshot campaigns:', err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load campaigns on component mount and when URL params change
  useEffect(() => {
    fetchReferenceData();
    
    const urlSnapshotMonth = searchParams.get('snapshotMonth');
    if (urlSnapshotMonth) {
      setSelectedSnapshotMonth(urlSnapshotMonth);
      fetchSnapshotCampaigns(urlSnapshotMonth);
    } else {
      // Redirect to current campaigns if no snapshot month
      navigate('/campaigns');
    }
  }, [searchParams, fetchSnapshotCampaigns, navigate]);

  // Filter campaigns based on search term and brand
  const filteredCampaigns = Array.isArray(campaigns) 
    ? campaigns.filter(campaign => {
        const campaignName = campaign.name || campaign.campaign_name || '';
        const brandName = brandsMap[campaign.brand_id] || campaign.brand_name || campaign.brand || '';
        const mediaTypeText = campaign.media_type || '';
        
        const matchesSearch = searchTerm === '' || 
          campaignName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          brandName.toLowerCase().includes(searchTerm.toLowerCase()) || 
          mediaTypeText.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesBrand = currentBrand === 'All' || brandName === currentBrand;
        
        return matchesSearch && matchesBrand;
      })
    : [];

  // Calculate saving regardless of status
  const calculateSaving = (campaign) => {
    if (campaign.saving_total !== undefined && campaign.saving_total !== null) {
      return parseFloat(campaign.saving_total) || 0;
    }
    try {
      const planningCost = parseFloat(campaign.planning_total || campaign.planning || 0) || 0;
      const actualCost = parseFloat(campaign.actual_total || campaign.actual || 0) || 0;
      return planningCost - actualCost;
    } catch {
      return 0;
    }
  };

  // Add saving calculation to campaigns
  const campaignsWithSaving = filteredCampaigns.map(campaign => {
    const saving = calculateSaving(campaign);
    return {
      ...campaign,
      saving: saving
    };
  });

  // Prioritize Heineken brand in filtered results
  const sortedCampaigns = [...campaignsWithSaving].sort((a, b) => {
    const aBrandName = brandsMap[a.brand_id] || a.brand_name || a.brand || '';
    const bBrandName = brandsMap[b.brand_id] || b.brand_name || b.brand || '';
    
    if (aBrandName === 'Heineken' && bBrandName !== 'Heineken') return -1;
    if (aBrandName !== 'Heineken' && bBrandName === 'Heineken') return 1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedCampaigns.length / rowsPerPage);
  const displayedCampaigns = sortedCampaigns.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Toggle expand/collapse of a campaign row
  const toggleRowExpand = async (campaignId) => {
    setExpandedRows((prev) => {
      const newExpandedRows = { ...prev };
      
      if (newExpandedRows[campaignId]) {
        newExpandedRows[campaignId] = false;
      } else {
        newExpandedRows[campaignId] = true;
        loadMediaTypes(campaignId);
      }
      
      return newExpandedRows;
    });
  };

  // Load media types for a campaign (snapshot version)
  const loadMediaTypes = async (campaignId) => {
    // For snapshot data, we don't need to fetch additional data
    // The media types are already available from the initial snapshot fetch
    // We just mark the row as processed for display
    setCampaigns(prevCampaigns => 
      prevCampaigns.map(campaign => {
        if (campaign.id === campaignId && !campaign.mediaTypesProcessed) {
          // Media types are already available from snapshot data
          // We just mark as processed to avoid redundant API calls
          return {
            ...campaign,
            mediaTypesProcessed: true
          };
        }
        return campaign;
      })
    );
    
    console.log(`Media types for campaign ${campaignId} are already available from snapshot data.`);
  };

  // Navigate to Excel-like view for a specific media type (snapshot version)
  const handleViewExcelView = (campaignId, mediaType) => {
    navigate(`/excel-view/${campaignId}/${mediaType}?snapshotMonth=${selectedSnapshotMonth}`);
  };

  // Show snackbar message
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle brand tab change
  const handleBrandChange = (event, newValue) => {
    setCurrentBrand(newValue);
    setPage(1);
  };

  // Handle snapshot month change
  const handleSnapshotMonthChange = useCallback((event) => {
    const month = event.target.value === 'live' ? null : event.target.value;
    
    if (!month) {
      // Navigate to current campaigns page
      navigate('/campaigns');
      return;
    }
    
    setSelectedSnapshotMonth(month);
    
    // Update URL with the selected snapshot month
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('snapshotMonth', month);
    setSearchParams(newSearchParams);
    
    // Clear expanded rows when switching snapshots
    setExpandedRows({});
    
    // Fetch new campaign data based on selected snapshot
    fetchSnapshotCampaigns(month);
    
    console.log(`Switched to snapshot data for ${month}`);
  }, [searchParams, setSearchParams, fetchSnapshotCampaigns, navigate]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Get columns based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['name', 'brand', 'status', 'actions'];
    } else if (isTablet) {
      return ['name', 'brand', 'type', 'status', 'budget', 'actions'];
    } else {
      return ['name', 'brand', 'subBrand', 'type', 'status', 'startDate', 'endDate', 'budget', 'planning', 'actual', 'saving', 'actions'];
    }
  };
  
  const visibleColumns = getVisibleColumns();

  // Rest of the component continues with render methods...
  // (Media type rows rendering, main render, etc.)
  
  return (
    <>
      <GlobalStyles
        styles={{
          'html, body, #root': {
            overflowX: 'hidden !important',
            maxWidth: '100vw !important',
            margin: 0,
            padding: 0,
          },
          '*': {
            boxSizing: 'border-box !important',
          },
          '.MuiContainer-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
            maxWidth: '100% !important',
            width: '100% !important',
            margin: '0 auto !important',
          },
        }}
      />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#f8fffe',
        py: { xs: 2, sm: 3 },
        px: 0,
        overflow: 'hidden',
        overflowY: 'auto',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box'
      }}>
        <Container maxWidth="xl" sx={{ 
          px: { xs: 1.5, sm: 2, md: 3 },
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto',
          boxSizing: 'border-box'
        }}>
          {/* Header Section with Snapshot indicator */}
          <Paper elevation={0} sx={{ 
            mb: { xs: 1.5, sm: 2, md: 2.5 },
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              zIndex: 0
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <CampaignIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h4" sx={{ 
                    fontWeight: 700, 
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    Campaign Management - Snapshot View
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    opacity: 0.9,
                    fontWeight: 400
                  }}>
                    Viewing snapshot data for {selectedSnapshotMonth} (Read-only mode)
                  </Typography>
                </Box>
              </Box>
              
              {/* Quick Stats and Snapshot Filter */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2, 
                mt: 2,
                flexWrap: 'wrap'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  flexWrap: 'wrap'
                }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 1,
                    p: 1.5,
                    minWidth: 120,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {sortedCampaigns.length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Total Campaigns
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 1,
                    p: 1.5,
                    minWidth: 120,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {sortedCampaigns.filter(c => determineCampaignStatus(c) === 'DONE').length}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Completed
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.15)',
                    borderRadius: 1,
                    p: 1.5,
                    minWidth: 120,
                    textAlign: 'center'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {new Set(sortedCampaigns.map(c => (c.brand_name || c.brand || '')).filter(Boolean)).size}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      Active Brands
                    </Typography>
                  </Box>
                </Box>
                
                {/* Snapshot Filter */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 1,
                  p: 1.5,
                  minWidth: 200
                }}>
                  <SnapshotCalendarSelector
                    availableSnapshots={snapshotMonths}
                    selectedSnapshot={selectedSnapshotMonth}
                    onSnapshotSelect={handleSnapshotMonthChange}
                    onLiveDataSelect={() => handleSnapshotMonthChange({ target: { value: 'live' } })}
                    sx={{
                      '& .MuiPaper-root': {
                        color: 'white',
                        fontSize: FONT_SIZES.caption,
                        height: '32px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.5)',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                        },
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* Brand tabs */}
          <Paper sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid rgba(76, 175, 80, 0.2)',
            backgroundColor: 'background.paper',
            mb: { xs: 1, sm: 1.5 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)'
          }}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)',
              borderBottom: '2px solid',
              borderColor: '#4caf50',
              px: 0,
              py: 0,
              width: '100%',
              overflow: 'hidden'
            }}>
              <Tabs 
                value={currentBrand}
                onChange={handleBrandChange}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  width: '100%',
                  minHeight: { xs: 32, sm: 36 },
                  '& .MuiTab-root': {
                    color: 'text.secondary',
                    fontWeight: 500,
                    fontSize: FONT_SIZES.body,
                    minHeight: { xs: 40, sm: 44 },
                    textTransform: 'none',
                    px: { xs: 1, sm: 1.5, md: 2 },
                    py: { xs: 0.5, sm: 0.75 },
                    minWidth: { xs: 'auto', sm: 70 },
                    maxWidth: { xs: 110, sm: 'none' },
                    borderRadius: '8px 8px 0 0',
                    margin: '0 2px',
                    transition: 'all 0.3s ease',
                    '&.Mui-selected': {
                      color: '#2e7d32',
                      fontWeight: 700,
                      backgroundColor: 'rgba(76, 175, 80, 0.12)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                    },
                    '&:hover': {
                      color: '#2e7d32',
                      backgroundColor: 'rgba(76, 175, 80, 0.06)',
                      transform: 'translateY(-1px)',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#4caf50',
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                  },
                }}
              >
                {brands.map((brand) => (
                  <Tab 
                    key={brand} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography 
                          component="span" 
                          sx={{ 
                            fontSize: 'inherit',
                            fontWeight: 'inherit'
                          }}
                        >
                          {isMobile && brand.length > 8 ? brand.substring(0, 6) + '...' : brand}
                        </Typography>
                      </Box>
                    }
                    value={brand} 
                  />
                ))}
              </Tabs>
            </Box>
          </Paper>

          {/* Search and action buttons - Read-only version */}
          <Paper sx={{ 
            p: { xs: 2, sm: 2.5 },
            borderRadius: 3,
            mb: { xs: 2, sm: 2.5 },
            backgroundColor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <TextField
                  placeholder="Search campaigns by name, brand, type..."
                  variant="outlined"
                  size="small"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  fullWidth
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: FONT_SIZES.body,
                      height: '44px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid rgba(0, 0, 0, 0.08)',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        boxShadow: '0 0 0 2px rgba(46, 125, 50, 0.2)',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: FONT_SIZES.body,
                      fontWeight: 400
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ 
                          fontSize: 20,
                          color: '#666'
                        }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack 
                  direction="row" 
                  spacing={1}
                  justifyContent={{ xs: 'center', md: 'flex-end' }}
                  sx={{ width: '100%' }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon sx={{ fontSize: 18 }} />}
                    onClick={() => fetchSnapshotCampaigns(selectedSnapshotMonth)}
                    sx={{ 
                      fontSize: FONT_SIZES.body,
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      height: '44px',
                      borderRadius: 2,
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      color: '#666',
                      backgroundColor: 'white',
                      textTransform: 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                        color: '#2e7d32',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/campaigns')}
                    sx={{ 
                      fontSize: FONT_SIZES.body,
                      fontWeight: 500,
                      px: 2.5,
                      py: 1,
                      height: '44px',
                      borderRadius: 2,
                      borderColor: '#2e7d32',
                      color: '#2e7d32',
                      backgroundColor: 'white',
                      textTransform: 'none',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
                      '&:hover': {
                        backgroundColor: '#2e7d32',
                        color: 'white',
                      }
                    }}
                  >
                    Switch to Live View
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Paper elevation={3} sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '300px',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              my: 4
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Loading snapshot campaigns...
                </Typography>
              </Box>
            </Paper>
          ) : displayedCampaigns.length === 0 ? (
            <Paper elevation={2} sx={{ 
              p: 4, 
              textAlign: 'center',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
            }}>
              <Box sx={{ mb: 2 }}>
                <CampaignIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
              </Box>
              <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
                No snapshot campaigns found
              </Typography>
              <Typography variant="body1" color="text.secondary">
                No campaigns data available for the selected snapshot month.
              </Typography>
            </Paper>
          ) : (
            <SnapshotCampaignsTable 
              campaigns={displayedCampaigns}
              visibleColumns={visibleColumns}
              expandedRows={expandedRows}
              toggleRowExpand={toggleRowExpand}
              handleViewExcelView={handleViewExcelView}
              getCampaignTypeDisplay={getCampaignTypeDisplay}
              calculateSaving={calculateSaving}
              theme={theme}
              FONT_SIZES={FONT_SIZES}
              brandsMap={brandsMap}
              subBrandsMap={subBrandsMap}
            />
          )}

          {/* Pagination */}
          {displayedCampaigns.length > 0 && (
            <Paper elevation={4} sx={{ 
              width: '100%', 
              overflow: 'hidden', 
              borderRadius: 2, 
              mb: { xs: 1, sm: 1.5 },
              border: 'none',
              backgroundColor: 'background.paper',
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              <Divider sx={{ borderColor: 'grey.200' }} />
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                p: { xs: 0.5, sm: 1 },
                gap: { xs: 0.5, sm: 0.25 },
                backgroundColor: 'grey.50',
                borderRadius: '0 0 6px 6px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontSize: FONT_SIZES.caption,
                    fontWeight: 500
                  }}>
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    size="small"
                    sx={{ 
                      height: { xs: 22, sm: 26 },
                      minWidth: { xs: 40, sm: 50 },
                      borderRadius: 0.5,
                      backgroundColor: 'background.paper',
                      fontSize: FONT_SIZES.caption,
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    <MenuItem value={10} sx={{ fontSize: 'inherit' }}>10</MenuItem>
                    <MenuItem value={25} sx={{ fontSize: 'inherit' }}>25</MenuItem>
                    <MenuItem value={50} sx={{ fontSize: 'inherit' }}>50</MenuItem>
                  </Select>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  width: { xs: '100%', sm: 'auto' },
                  gap: { xs: 0.25, sm: 0.5 }
                }}>
                  <Typography variant="caption" color="text.secondary" sx={{
                    fontSize: FONT_SIZES.caption,
                    fontWeight: 500,
                    whiteSpace: { xs: 'normal', sm: 'nowrap' }
                  }}>
                    {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, sortedCampaigns.length)} of ${sortedCampaigns.length}`}
                  </Typography>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handlePageChange} 
                    shape="rounded" 
                    size="small"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: FONT_SIZES.caption,
                        minWidth: { xs: 20, sm: 24 },
                        height: { xs: 20, sm: 24 },
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          borderColor: alpha(theme.palette.primary.main, 0.2),
                        },
                        '&.Mui-selected': {
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          fontWeight: 600,
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            </Paper>
          )}

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={5000}
            onClose={handleCloseSnackbar}
          >
            <Alert 
              onClose={handleCloseSnackbar} 
              severity={snackbar.severity} 
              variant="filled"
              sx={{
                ...(snackbar.severity === 'success' && {
                  bgcolor: theme.palette.primary.main
                })
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

// Separate component for snapshot campaigns table
const SnapshotCampaignsTable = ({ 
  campaigns, 
  visibleColumns, 
  expandedRows, 
  toggleRowExpand, 
  handleViewExcelView,
  getCampaignTypeDisplay,
  calculateSaving,
  theme,
  FONT_SIZES,
  brandsMap = {},
  subBrandsMap = {}
}) => {
  // Render media type rows for a campaign (snapshot version)
  const renderMediaTypeRows = (campaign) => {
    let mediaTypes = campaign.media_types || campaign.mediaTypes || [];
    
    if (mediaTypes && typeof mediaTypes === 'object' && !Array.isArray(mediaTypes)) {
      mediaTypes = Object.values(mediaTypes);
    }
    
    if (!Array.isArray(mediaTypes)) {
      mediaTypes = [];
    }
    
    // Deduplicate media types by media_type name and aggregate costs
    const uniqueMediaTypes = {};
    mediaTypes.forEach(mt => {
      if (!mt || !mt.media_type) return;
      
      const key = mt.media_type;
      
      let totalGiven = 0;
      let totalPlanning = 0;
      let totalActual = 0;
      
      if (mt.monthly_data && Array.isArray(mt.monthly_data)) {
        mt.monthly_data.forEach(monthData => {
          const givenValue = monthData.given_cost || monthData.Given_Cost_Excl_VAT_VND || monthData.given;
          const planningValue = monthData.Planning_Gross_Cost_Excl_VAT_VND || monthData.planning_gross_cost;
          const actualValue = monthData.Actual_Gross_Cost_Excl_VAT_VND || monthData.actual_gross_cost;
          
          if (givenValue && givenValue !== 'nan' && !isNaN(parseFloat(givenValue))) {
            totalGiven += parseFloat(givenValue);
          }
          if (planningValue && planningValue !== 'nan' && !isNaN(parseFloat(planningValue))) {
            totalPlanning += parseFloat(planningValue);
          }
          if (actualValue && actualValue !== 'nan' && !isNaN(parseFloat(actualValue))) {
            totalActual += parseFloat(actualValue);
          }
        });
      } else {
        // Fallback to media type level values
        totalGiven = parseFloat(mt.media_type_given || mt.Given_Cost_Excl_VAT_VND || mt.given || 0);
        totalPlanning = parseFloat(mt.Planning_Gross_Cost_Excl_VAT_VND || mt.planning_gross_cost || 0);
        totalActual = parseFloat(mt.Actual_Gross_Cost_Excl_VAT_VND || mt.actual_gross_cost || 0);
      }
      
      if (!uniqueMediaTypes[key]) {
        uniqueMediaTypes[key] = {
          id: mt.id,
          media_type: mt.media_type,
          given: totalGiven,
          planning_gross_cost: totalPlanning,
          actual_gross_cost: totalActual,
          monthly_data: mt.monthly_data || [] // Copy monthly_data để có thể truy cập NTW
        };
      } else {
        uniqueMediaTypes[key].given += totalGiven;
        uniqueMediaTypes[key].planning_gross_cost += totalPlanning;
        uniqueMediaTypes[key].actual_gross_cost += totalActual;
        // Merge monthly_data arrays
        if (mt.monthly_data && Array.isArray(mt.monthly_data)) {
          uniqueMediaTypes[key].monthly_data = uniqueMediaTypes[key].monthly_data || [];
          uniqueMediaTypes[key].monthly_data.push(...mt.monthly_data);
        }
      }
    });
    
    mediaTypes = Object.values(uniqueMediaTypes);

    if (mediaTypes.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={12} align="center">
            <Typography variant="body2" color="textSecondary">
              No media types available
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    const campaignBrandName = (subBrandsMap && campaign.sub_brand_id ? subBrandsMap[campaign.sub_brand_id] : null) 
      || (brandsMap && campaign.brand_id ? brandsMap[campaign.brand_id] : null)
      || campaign.brand_name || campaign.brand || '';
    const campaignStatus = determineCampaignStatus(campaign);
    const isDone = campaignStatus === 'DONE';

    return (
      <React.Fragment>
        <TableRow>
          <TableCell colSpan={12} sx={{ 
            paddingBottom: 0, 
            paddingTop: 0,
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0'
          }}>
            <Box sx={{ 
              p: { xs: 2, sm: 3 },
              m: { xs: 1, sm: 2 },
              borderRadius: 2,
              backgroundColor: 'white',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              position: 'relative'
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                mb: 3, 
                color: '#2e7d32',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontSize: { xs: '1rem', sm: '1.1rem' }
              }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: '#4caf50' 
                }} />
                Media Types for {campaign.campaign_name || campaign.name} ({campaignBrandName}) - Snapshot View
              </Typography>
              
              <Box sx={{ 
                overflowX: 'auto',
                borderRadius: 1.5,
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.04)',
                border: '1px solid rgba(0, 0, 0, 0.06)'
              }}>
                <Table size="small" sx={{ 
                  backgroundColor: 'white',
                  '& .MuiTableCell-root': {
                    fontSize: FONT_SIZES.table,
                    borderColor: 'rgba(0, 0, 0, 0.08)',
                    py: { xs: 1, sm: 1.2 },
                    px: { xs: 1, sm: 1.5 }
                  }
                }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: '#f8f9fa',
                      '& .MuiTableCell-head': {
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: FONT_SIZES.tableHeader,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        backgroundColor: '#f8f9fa'
                      }
                    }}>
                      <TableCell>Media Type</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Planning Gross Cost</TableCell>
                      <TableCell>Actual Gross Cost</TableCell>
                      <TableCell>NTW total</TableCell> {/* Thêm cột này */}
                      <TableCell>Saving</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mediaTypes.map((mediaType) => {
                      const planningCost = parseFloat(mediaType.planning_gross_cost || 0);
                      const actualCost = parseFloat(mediaType.actual_gross_cost || 0);
                      const mediaSaving = isDone ? planningCost - actualCost : 0;
                      // Tính tổng NTW cho mediaType
                      let ntwTotal = 0;
                      console.log(`Debug mediaType for ${mediaType.media_type}:`, mediaType);
                      console.log(`monthly_data exists:`, !!mediaType.monthly_data);
                      console.log(`monthly_data is array:`, Array.isArray(mediaType.monthly_data));
                      console.log(`monthly_data length:`, mediaType.monthly_data?.length);

                      if (mediaType.monthly_data && Array.isArray(mediaType.monthly_data)) {
                        console.log(`Processing ${mediaType.monthly_data.length} monthly records for ${mediaType.media_type}`);
                        ntwTotal = mediaType.monthly_data.reduce((sum, md) => {
                          const ntwValue = parseFloat(md.ntw || md.NTW || 0);
                          console.log(`NTW value for ${mediaType.media_type}:`, ntwValue, 'from:', md.ntw || md.NTW);
                          return sum + ntwValue;
                        }, 0);
                      } else if (mediaType.ntw !== undefined) {
                        ntwTotal = parseFloat(mediaType.ntw) || 0;
                        console.log(`Using direct ntw value for ${mediaType.media_type}:`, ntwTotal);
                      } else {
                        console.log(`No NTW data found for ${mediaType.media_type}`);
                      }
                      console.log(`Total NTW for ${mediaType.media_type}:`, ntwTotal);
                      return (
                        <TableRow key={`${campaign.id}-${mediaType.id || mediaType.media_type}`}>
                          <TableCell>
                            <Chip
                              label={mediaType.media_type}
                              sx={{ 
                                fontWeight: 500,
                                minWidth: 70,
                                textAlign: 'center',
                                fontSize: FONT_SIZES.table,
                                borderRadius: 1.5,
                                backgroundColor: mediaType.media_type === 'Digital' ? '#f0f7ff' :
                                                mediaType.media_type === 'OOH' ? '#f0f9f0' :
                                                mediaType.media_type === 'TV' ? '#fff8f0' :
                                                mediaType.media_type === 'Radio' ? '#f8f0ff' : '#f5f5f5',
                                color: mediaType.media_type === 'Digital' ? '#1976d2' :
                                       mediaType.media_type === 'OOH' ? '#2e7d32' :
                                       mediaType.media_type === 'TV' ? '#f57c00' :
                                       mediaType.media_type === 'Radio' ? '#7b1fa2' : '#616161',
                                border: `1px solid ${
                                  mediaType.media_type === 'Digital' ? 'rgba(25, 118, 210, 0.2)' :
                                  mediaType.media_type === 'OOH' ? 'rgba(46, 125, 50, 0.2)' :
                                  mediaType.media_type === 'TV' ? 'rgba(245, 124, 0, 0.2)' :
                                  mediaType.media_type === 'Radio' ? 'rgba(123, 31, 162, 0.2)' : 'rgba(0, 0, 0, 0.1)'
                                }`,
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: FONT_SIZES.number,
                              fontWeight: 500,
                              color: '#2e7d32'
                            }}>
                              {mediaType.given ? formatCurrency(parseFloat(mediaType.given)) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: FONT_SIZES.number,
                              fontWeight: 500,
                              color: '#2e7d32'
                            }}>
                              {mediaType.planning_gross_cost ? formatCurrency(parseFloat(mediaType.planning_gross_cost)) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ 
                              fontSize: FONT_SIZES.number,
                              fontWeight: 500,
                              color: '#2e7d32'
                            }}>
                              {mediaType.actual_gross_cost ? formatCurrency(parseFloat(mediaType.actual_gross_cost)) : '0 đ'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: FONT_SIZES.number, fontWeight: 500, color: '#2e7d32' }}>
                              {formatCurrency(ntwTotal)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: FONT_SIZES.number,
                                color: mediaSaving > 0 
                                  ? '#2e7d32' 
                                  : mediaSaving < 0 
                                    ? '#d32f2f' 
                                    : '#616161'
                              }}
                            >
                              {isDone ? formatCurrency(mediaSaving) : '-'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`View ${mediaType.media_type} data (read-only)`}>
                              <IconButton
                                size="small"
                                onClick={() => handleViewExcelView(campaign.id, mediaType.media_type)}
                                sx={{ 
                                  color: '#2e7d32',
                                  bgcolor: 'rgba(46, 125, 50, 0.06)', 
                                  border: '1px solid rgba(46, 125, 50, 0.2)',
                                  borderRadius: 1.5,
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                  '&:hover': { 
                                    bgcolor: 'rgba(46, 125, 50, 0.12)',
                                    borderColor: 'rgba(46, 125, 50, 0.3)',
                                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)'
                                  } 
                                }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Row tổng cho bảng media types */}
                    <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiTableCell-root': { fontWeight: 'bold', borderTop: '2px solid #1976d2', padding: '8px 12px', fontSize: '0.7rem', color: 'black' } }}>
                      <TableCell>TOTAL</TableCell>
                      <TableCell>
                        {formatCurrency(mediaTypes.reduce((sum, mt) => sum + (parseFloat(mt.given || 0)), 0))}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(mediaTypes.reduce((sum, mt) => sum + (parseFloat(mt.planning_gross_cost || 0)), 0))}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(mediaTypes.reduce((sum, mt) => sum + (parseFloat(mt.actual_gross_cost || 0)), 0))}
                      </TableCell>
                      <TableCell>
                        {formatCurrency(mediaTypes.reduce((sum, mt) => {
                          if (mt.monthly_data && Array.isArray(mt.monthly_data)) {
                            return sum + mt.monthly_data.reduce((s, md) => s + (parseFloat(md.ntw || 0)), 0);
                          } else if (mt.ntw !== undefined) {
                            return sum + (parseFloat(mt.ntw) || 0);
                          }
                          return sum;
                        }, 0))}
                      </TableCell>
                      <TableCell>
                        {isDone ? formatCurrency(mediaTypes.reduce((sum, mt) => sum + (parseFloat(mt.planning_gross_cost || 0) - parseFloat(mt.actual_gross_cost || 0)), 0)) : '-'}
                      </TableCell>
                      <TableCell align="center">-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Paper elevation={4} sx={{ 
      width: '100%', 
      overflow: 'hidden', 
      overflowX: 'hidden',
      borderRadius: 2, 
      mb: { xs: 1, sm: 1.5 },
      border: 'none',
      backgroundColor: 'background.paper',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      maxWidth: '100%',
      boxSizing: 'border-box'
    }}>
      <TableContainer sx={{ 
        width: '100%', 
        overflowX: 'auto',
        maxWidth: '100%',
      }}>
        <Table sx={{ 
          minWidth: { xs: 400, sm: 480, md: 560 },
          width: '100%',
          '& .MuiTableCell-root': {
            fontSize: FONT_SIZES.table,
            py: { xs: 0.3, sm: 0.4 },
            px: { xs: 0.3, sm: 0.4 },
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }}>
          <TableHead>
            <TableRow sx={{ 
              background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
              '& .MuiTableCell-head': {
                fontWeight: 700,
                fontSize: FONT_SIZES.tableHeader,
                color: 'black', // Đổi từ 'white' sang 'black'
                borderBottom: 'none',
                py: { xs: 0.75, sm: 1 },
                px: { xs: 0.5, sm: 0.75 },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textShadow: 'none' // Bỏ hiệu ứng bóng chữ
              }
            }}>
              <TableCell padding="checkbox" width="24px">
                <Typography variant="caption" fontWeight="700" sx={{
                  fontSize: FONT_SIZES.tableHeader,
                  color: 'white'
                }}>
                  ⚡
                </Typography>
              </TableCell>
              {visibleColumns.includes('name') && (
                <TableCell sx={{ minWidth: { xs: 80, sm: 95 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Campaign Name
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('brand') && (
                <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Brand
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('subBrand') && (
                <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Sub-Brand
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('type') && (
                <TableCell sx={{ minWidth: { xs: 40, sm: 50 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Type
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('status') && (
                <TableCell sx={{ minWidth: { xs: 45, sm: 60 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Status
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('startDate') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Start Date
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('endDate') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    End Date
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('budget') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Budget
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('planning') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Planning Cost
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('actual') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 80 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Actual Cost
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('saving') && (
                <TableCell sx={{ minWidth: { xs: 60, sm: 95 } }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Saving (Done Only)
                  </Typography>
                </TableCell>
              )}
              {visibleColumns.includes('actions') && (
                <TableCell align="center" width="40px">
                  <Typography variant="subtitle2" fontWeight="600" sx={{ fontSize: 'inherit' }}>
                    Actions
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map((campaign) => (
              <React.Fragment key={campaign.id}>
                <TableRow 
                  hover
                  sx={{ 
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                    ...(campaign.brand_name === 'Heineken' && {
                      bgcolor: alpha(theme.palette.primary.lighter, 0.4),
                    }),
                  }}
                >
                  <TableCell padding="checkbox">
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => toggleRowExpand(campaign.id)}
                      sx={{ 
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {expandedRows[campaign.id] ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  {visibleColumns.includes('name') && (
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: { xs: '120px', sm: '150px' }
                      }}>
                        {campaign.name || campaign.campaign_name || `Campaign ${campaign.id}`}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('brand') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {campaign.brand_name || campaign.brand || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('subBrand') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {campaign.sub_brand_name || campaign.sub_brand || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('type') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {getCampaignTypeDisplay(campaign.campaign_type || campaign.type)}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('status') && (
                    <TableCell>
                      <Chip 
                        label={determineCampaignStatus(campaign)} 
                        size="small"
                        sx={{
                          backgroundColor: 
                            determineCampaignStatus(campaign) === 'DONE'
                              ? '#9e9e9e'
                              : determineCampaignStatus(campaign) === 'ON-GOING'
                              ? '#4caf50'
                              : '#ffc107',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: FONT_SIZES.table,
                          height: '22px'
                        }} 
                      />
                    </TableCell>
                  )}
                  {visibleColumns.includes('startDate') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {campaign.start_date 
                          ? format(new Date(campaign.start_date), 'dd MMMM y') 
                          : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('endDate') && (
                    <TableCell>
                      <Typography variant="body2" sx={{ 
                        fontSize: FONT_SIZES.table,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {campaign.end_date 
                          ? format(new Date(campaign.end_date), 'dd MMMM y')
                          : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('budget') && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontWeight: 500, 
                          fontSize: FONT_SIZES.number,
                          color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {campaign.total_given ? formatCurrency(parseFloat(campaign.total_given)) :
                         campaign.given_lc ? formatCurrency(parseFloat(campaign.given_lc)) :
                         campaign.given_cost ? formatCurrency(parseFloat(campaign.given_cost)) :
                         campaign.Given_Cost_Excl_VAT_VND ? formatCurrency(parseFloat(campaign.Given_Cost_Excl_VAT_VND)) : '0 đ'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('planning') && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontWeight: 500, 
                          fontSize: FONT_SIZES.number,
                          color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {campaign.planning_total ? formatCurrency(parseFloat(campaign.planning_total)) :
                         campaign.planning_gross_cost ? formatCurrency(parseFloat(campaign.planning_gross_cost)) :
                         campaign.planning_gross_cost_lc ? formatCurrency(parseFloat(campaign.planning_gross_cost_lc)) :
                         campaign.Planning_Gross_Cost_Excl_VAT_VND ? formatCurrency(parseFloat(campaign.Planning_Gross_Cost_Excl_VAT_VND)) :
                         campaign.planning_cost ? formatCurrency(parseFloat(campaign.planning_cost)) : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('actual') && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ 
                          fontWeight: 500, 
                          fontSize: FONT_SIZES.number,
                          color: campaign.brand === 'Heineken' ? theme.palette.primary.dark : 'inherit',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {campaign.actual_total ? formatCurrency(parseFloat(campaign.actual_total)) :
                         campaign.actual_gross_cost ? formatCurrency(parseFloat(campaign.actual_gross_cost)) :
                         campaign.actual_gross_cost_lc ? formatCurrency(parseFloat(campaign.actual_gross_cost_lc)) :
                         campaign.Actual_Gross_Cost_Excl_VAT_VND ? formatCurrency(parseFloat(campaign.Actual_Gross_Cost_Excl_VAT_VND)) :
                         campaign.actual_cost ? formatCurrency(parseFloat(campaign.actual_cost)) : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('saving') && (
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: FONT_SIZES.number,
                          color: campaign.saving > 0 
                            ? theme.palette.success.main 
                            : campaign.saving < 0 
                              ? theme.palette.error.main 
                              : 'inherit',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {determineCampaignStatus(campaign) === 'DONE' 
                          ? formatCurrency(calculateSaving(campaign)) 
                          : '-'}
                      </Typography>
                    </TableCell>
                  )}
                  {visibleColumns.includes('actions') && (
                    <TableCell align="center">
                      <Tooltip title="View campaign details (read-only)">
                        <IconButton 
                          size="small"
                          onClick={() => toggleRowExpand(campaign.id)}
                          sx={{ 
                            color: theme.palette.text.secondary,
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main
                            } 
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
                {expandedRows[campaign.id] && renderMediaTypeRows(campaign)}
              </React.Fragment>
            ))}
            {/* Total Row */}
            <TableRow sx={{ backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiTableCell-root': { fontWeight: 'bold', borderTop: '2px solid #1976d2', padding: '8px 12px', fontSize: '0.7rem', color: 'black' } }}>
              <TableCell padding="checkbox">
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.7rem', color: 'black' }}>
                  Σ
                </Typography>
              </TableCell>
              {visibleColumns.includes('name') && (
                <TableCell sx={{ minWidth: 160, fontWeight: 'bold', fontSize: '0.7rem', color: 'black' }}>
                  TOTAL ({campaigns.length} campaigns)
                </TableCell>
              )}
              {visibleColumns.includes('brand') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  All Brands
                </TableCell>
              )}
              {visibleColumns.includes('subBrand') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  All Sub-brands
                </TableCell>
              )}
              {visibleColumns.includes('type') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  All Types
                </TableCell>
              )}
              {visibleColumns.includes('status') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  All Status
                </TableCell>
              )}
              {visibleColumns.includes('startDate') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  -
                </TableCell>
              )}
              {visibleColumns.includes('endDate') && (
                <TableCell sx={{ fontSize: '0.65rem', color: 'black' }}>
                  -
                </TableCell>
              )}
              {visibleColumns.includes('budget') && (
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'black' }}>
                  {formatCurrency(campaigns.reduce((sum, campaign) => {
                    const budgetValue = parseFloat(
                      campaign.total_given || 
                      campaign.given_lc || 
                      campaign.given_cost || 
                      campaign.Given_Cost_Excl_VAT_VND || 
                      0
                    );
                    return sum + budgetValue;
                  }, 0))}
                </TableCell>
              )}
              {visibleColumns.includes('planning') && (
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'black' }}>
                  {formatCurrency(campaigns.reduce((sum, campaign) => {
                    const planningValue = parseFloat(
                      campaign.planning_total || 
                      campaign.planning_gross_cost || 
                      campaign.planning_gross_cost_lc || 
                      campaign.Planning_Gross_Cost_Excl_VAT_VND || 
                      campaign.planning_cost || 
                      0
                    );
                    return sum + planningValue;
                  }, 0))}
                </TableCell>
              )}
              {visibleColumns.includes('actual') && (
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'black' }}>
                  {formatCurrency(campaigns.reduce((sum, campaign) => {
                    const actualValue = parseFloat(
                      campaign.actual_total || 
                      campaign.actual_gross_cost || 
                      campaign.actual_gross_cost_lc || 
                      campaign.Actual_Gross_Cost_Excl_VAT_VND || 
                      campaign.actual_cost || 
                      0
                    );
                    return sum + actualValue;
                  }, 0))}
                </TableCell>
              )}
              {visibleColumns.includes('saving') && (
                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.65rem', minWidth: 100, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: (() => {
                  const totalSaving = campaigns.reduce((sum, campaign) => sum + calculateSaving(campaign), 0);
                  return totalSaving > 0 ? '#2e7d32' : totalSaving < 0 ? '#d32f2f' : 'black';
                })() }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: FONT_SIZES.number,
                    color: (() => {
                      const totalSaving = campaigns.reduce((sum, campaign) => sum + calculateSaving(campaign), 0);
                      return totalSaving > 0 ? '#2e7d32' : totalSaving < 0 ? '#d32f2f' : 'inherit';
                    })(),
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {formatCurrency(campaigns.reduce((sum, campaign) => sum + calculateSaving(campaign), 0))}
                </Typography>
              </TableCell>
            )}
            {visibleColumns.includes('actions') && (
              <TableCell align="center" sx={{ fontSize: '0.65rem', color: 'black' }}>
                -
              </TableCell>
            )}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);
};

export default CampaignsSnapshot; 